import { getCardsPool } from "../shared/cards-pool.js";
import {
  loadDeck, loadCompositions, saveComposition, deleteComposition,
  countCardUsage, newId, newInstanceId,
} from "./storage.js";
import { renderCanvasCard, renderTrayCard } from "./card-render.js";

export async function openEditor({ rootEl, compId, onClose, onShare }) {
  const cards = await getCardsPool();
  const comps = loadCompositions();
  let state;
  if (compId) {
    const existing = comps.find((c) => c.id === compId);
    if (!existing) return;
    state = JSON.parse(JSON.stringify(existing));
  } else {
    state = { id: newId(), title: "", description: "", cards: [], createdAt: Date.now(), updatedAt: Date.now() };
  }

  rootEl.innerHTML = renderShell();
  rootEl.classList.add("open");

  const elTitle = rootEl.querySelector(".editor-title-input");
  const elDesc = rootEl.querySelector(".editor-desc-input");
  const elCanvas = rootEl.querySelector(".editor-canvas");
  const elCount = rootEl.querySelector("#canvas-card-count");
  const elTray = rootEl.querySelector(".tray-grid");
  const elShare = rootEl.querySelector("#editor-share");
  const elSave = rootEl.querySelector("#editor-save");
  const elDelete = rootEl.querySelector("#editor-delete");
  const elClose = rootEl.querySelector("#editor-close");

  elTitle.value = state.title;
  elDesc.value = state.description;

  function renderCanvas() {
    elCanvas.querySelectorAll(".cv-card").forEach((n) => n.remove());
    elCount.textContent = state.cards.length;
    if (state.cards.length === 0) {
      let empty = elCanvas.querySelector(".canvas-empty");
      if (!empty) {
        empty = document.createElement("div");
        empty.className = "canvas-empty";
        empty.textContent = "Drag cards here";
        elCanvas.appendChild(empty);
      }
      return;
    }
    const empty = elCanvas.querySelector(".canvas-empty"); if (empty) empty.remove();
    for (const c of state.cards) {
      const card = cards[c.suit][c.index];
      const el = renderCanvasCard({ ...card, suit: c.suit });
      el.style.left = c.x + "px";
      el.style.top = c.y + "px";
      el.dataset.instanceId = c.instanceId;
      const removeBtn = document.createElement("button");
      removeBtn.className = "cv-remove";
      removeBtn.setAttribute("aria-label", "Remove");
      removeBtn.textContent = "×";
      el.appendChild(removeBtn);
      attachCvDrag(el, c);
      removeBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        state.cards = state.cards.filter((x) => x.instanceId !== c.instanceId);
        renderCanvas();
        renderTray();
      });
      elCanvas.appendChild(el);
    }
  }

  function attachCvDrag(el, cardRef) {
    let sx = 0, sy = 0, ox = 0, oy = 0, dragging = false;
    el.addEventListener("pointerdown", (e) => {
      if (e.target.classList.contains("cv-remove")) return;
      dragging = true; sx = e.clientX; sy = e.clientY; ox = cardRef.x; oy = cardRef.y;
      el.setPointerCapture(e.pointerId); el.classList.add("dragging");
    });
    el.addEventListener("pointermove", (e) => {
      if (!dragging) return;
      const dx = e.clientX - sx, dy = e.clientY - sy;
      const nx = Math.max(0, Math.min(639 - 130, ox + dx));
      const ny = Math.max(0, Math.min(399 - 170, oy + dy));
      el.style.left = nx + "px"; el.style.top = ny + "px";
      cardRef.x = nx; cardRef.y = ny;
    });
    function up() { dragging = false; el.classList.remove("dragging"); }
    el.addEventListener("pointerup", up); el.addEventListener("pointercancel", up);
  }

  function renderTray() {
    const deck = loadDeck();
    const usage = countCardUsage();
    const flat = [];
    ["action", "observation", "relation"].forEach((suit) => {
      Object.keys(deck[suit] || {}).forEach((idx) => {
        const i = parseInt(idx, 10);
        const owned = deck[suit][i];
        if (owned > 0) flat.push({ suit, index: i, card: cards[suit][i], owned });
      });
    });
    elTray.innerHTML = "";
    for (const item of flat) {
      const k = `${item.suit}:${item.index}`;
      const remaining = item.owned - (usage[k] || 0);
      const t = renderTrayCard({ ...item.card, suit: item.suit });
      if (remaining <= 0) t.classList.add("depleted");
      t.draggable = remaining > 0;
      const countEl = document.createElement("span");
      countEl.className = "tray-count";
      countEl.textContent = `${remaining}/${item.owned}`;
      t.appendChild(countEl);
      t.addEventListener("dragstart", (e) => {
        if (remaining <= 0) { e.preventDefault(); return; }
        e.dataTransfer.setData("text/dst-card", `${item.suit}:${item.index}`);
      });
      elTray.appendChild(t);
    }
    if (flat.length === 0) {
      elTray.innerHTML = '<div class="empty" style="grid-column:1/-1;">No cards yet. Close and open a pack.</div>';
    }
  }

  elCanvas.addEventListener("dragover", (e) => { e.preventDefault(); elCanvas.classList.add("dragover"); });
  elCanvas.addEventListener("dragleave", () => elCanvas.classList.remove("dragover"));
  elCanvas.addEventListener("drop", (e) => {
    e.preventDefault();
    elCanvas.classList.remove("dragover");
    const payload = e.dataTransfer.getData("text/dst-card");
    if (!payload || !payload.includes(":")) return;
    const [suit, idxStr] = payload.split(":");
    const index = parseInt(idxStr, 10);
    const usage = countCardUsage();
    const k = `${suit}:${index}`;
    const owned = (loadDeck()[suit] || {})[index] || 0;
    if ((usage[k] || 0) >= owned) return;
    const rect = elCanvas.getBoundingClientRect();
    const x = Math.max(0, Math.min(639 - 130, e.clientX - rect.left - 65));
    const y = Math.max(0, Math.min(399 - 170, e.clientY - rect.top - 85));
    state.cards.push({ suit, index, x, y, instanceId: newInstanceId() });
    renderCanvas();
    renderTray();
  });

  function close() {
    rootEl.classList.remove("open");
    rootEl.innerHTML = "";
    onClose?.();
  }
  function save() {
    state.title = elTitle.value;
    state.description = elDesc.value;
    state.updatedAt = Date.now();
    saveComposition(state);
  }

  elClose.addEventListener("click", () => { save(); close(); });
  elSave.addEventListener("click", () => { save(); });
  elDelete.addEventListener("click", () => {
    if (confirm("Delete this composition?")) { deleteComposition(state.id); close(); }
  });
  elShare.addEventListener("click", () => { save(); onShare(state); });

  renderCanvas();
  renderTray();
}

function renderShell() {
  return `
    <div class="editor">
      <div class="editor-head">
        <div class="editor-title-group">
          <input class="editor-title-input" placeholder="Name this time" maxlength="80">
          <input class="editor-desc-input" placeholder="What is this time?" maxlength="300">
        </div>
        <div class="editor-actions">
          <button class="editor-btn danger" id="editor-delete">Delete</button>
          <button class="editor-btn" id="editor-save">Save</button>
          <button class="editor-btn primary" id="editor-share">Share to createdtime</button>
          <button class="editor-btn" id="editor-close">Close</button>
        </div>
      </div>
      <div class="editor-body">
        <div class="editor-canvas-wrap">
          <div class="editor-canvas" style="width:640px;height:400px;position:relative;margin:0 auto;"></div>
          <div class="editor-canvas-foot"><span id="canvas-card-count">0</span> cards on canvas (640×400)</div>
        </div>
        <div class="editor-tray">
          <div class="editor-tray-head"><span class="section-title">Your cards</span></div>
          <div class="tray-grid"></div>
        </div>
      </div>
    </div>
  `;
}

function escapeHtml(s) {
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
